import React from 'react';
import { Box, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import { vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';

const RetroSecurityBadge = ({ userName, userEmail, userPhoto, isAdmin }) => {
    // Generate a pseudo-random clearance ID from email
    const clearanceId = userEmail
        ? `VX-${userEmail.split('@')[0].slice(0, 3).toUpperCase()}-${Math.abs(userEmail.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 9000 + 1000)}`
        : 'VX-OPR-0000';

    return (
        <Box sx={{
            background: 'linear-gradient(180deg, rgba(20, 25, 23, 0.9) 0%, rgba(10, 15, 13, 0.95) 100%)',
            border: '1px solid rgba(178, 140, 80, 0.4)',
            padding: 1.5,
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Top stripe */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: isAdmin
                    ? `linear-gradient(90deg, ${vortxColors.error}, #c0392b)`
                    : `linear-gradient(90deg, ${vortxColors.gold}, #8b6914)`,
            }} />

            {/* Badge content */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {/* Avatar */}
                <Box sx={{
                    width: 40,
                    height: 40,
                    border: '2px solid rgba(178, 140, 80, 0.6)',
                    background: 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 0 10px rgba(178, 140, 80, 0.2)',
                }}>
                    {userPhoto ? (
                        <Box
                            component="img"
                            src={userPhoto}
                            alt={userName || 'User'}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                filter: 'grayscale(30%) contrast(1.1)',
                            }}
                        />
                    ) : (
                        <Person sx={{ fontSize: 22, color: retroColors.dimmed }} />
                    )}
                    {/* Scanline overlay on photo */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 3px)',
                        pointerEvents: 'none',
                    }} />
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#f4f1ec',
                        textTransform: 'uppercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {userName || 'Operator'}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.55rem',
                        color: retroColors.muted,
                        letterSpacing: '0.1em',
                    }}>
                        {clearanceId}
                    </Typography>
                </Box>
            </Box>

            {/* Clearance level */}
            <Box sx={{
                mt: 1,
                pt: 1,
                borderTop: '1px solid rgba(63, 106, 100, 0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.5rem',
                    color: retroColors.dimmed,
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                }}>
                    Clearance
                </Typography>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: isAdmin ? vortxColors.error : retroColors.accent,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    textShadow: isAdmin ? '0 0 8px rgba(231, 76, 60, 0.5)' : '0 0 8px rgba(95, 212, 196, 0.5)',
                }}>
                    {isAdmin ? 'Level 5 / Admin' : 'Level 3 / Standard'}
                </Typography>
            </Box>
        </Box>
    );
};

export default RetroSecurityBadge;
